// Settings.jsx
import React, { useState, useEffect, useCallback } from 'react';
import SettingCard from '../components/Settings/SettingCard';
import WorkspaceProfileSection from '../components/Settings/WorkspaceProfileSection';
import settingsConfig from '../components/Settings/settingsConfig.json';
import Header from '../components/Header'
import { useNavigate, useLocation } from 'react-router-dom';
import './Settings.css';
import { useApi } from '../hooks/useApi'

const LOCAL_CARD_OVERRIDES = settingsConfig.sections.flatMap((section) =>
  section.cards.map((card) => ({
    ...card,
    section: section.id,
    enabled: card.disabled === false,
    comingSoonText: card.comingSoonText || null,
  }))
);

const LOCAL_CARDS_BY_ID = Object.fromEntries(
  LOCAL_CARD_OVERRIDES.map((card) => [card.id, card])
);

function mergeSettingCards(apiCards) {
  const merged = [];
  const seen = new Set();

  (apiCards || []).forEach((card) => {
    if (card.id === 'api-keys' || card.id === 'Workspaces') {
      return;
    }
    const override = LOCAL_CARDS_BY_ID[card.id];
    merged.push(override ? { ...card, ...override, id: card.id } : card);
    seen.add(card.id);
  });

  LOCAL_CARD_OVERRIDES.forEach((localCard) => {
    if (localCard.id === 'Workspaces') return;
    if (!seen.has(localCard.id)) {
      merged.push(localCard);
    }
  });

  return merged;
}

const Settings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [results, setResults] = useState([]); // resultados de la query
  const [sections, setSections] = useState([]);
  const { callApi, loading, error } = useApi();

  const handleCardClick = (cardId) => {
    const path = cardId === 'api-keys' ? '/api' : `/${cardId}`;
    navigate(path);
  };

  const SETTINS_CARD_QUERY = ` 
    query ListSettingCard {
        listSettingCard {
            totalCount
            items {
                id
                comingSoon
                comingSoonText
                description
                enabled
                section
                title
                variant
                icon {
                    path
                    strokeWidth
                    type
                    viewBox
                }
            }
        }
      }`

  const SETTING_CARDS_SECTIONS = `query ListSettingCard {
                listSettingCard(
                    uniqueField: "section"
                ) {
                    totalCount
                    items {
                        section
                    }
                }
            }`

  // Función para refrescar los datos
  const refreshCardSettings = useCallback(async () => {
    try {
      const data = await callApi(SETTINS_CARD_QUERY);
      const sectionList = await callApi(SETTING_CARDS_SECTIONS)
      const merged = mergeSettingCards(data.listSettingCard.items);
      setResults(merged);
      const sectionsFromCards = [...new Set(merged.map((c) => c.section).filter(Boolean))];
      setSections(
        sectionsFromCards.length
          ? sectionsFromCards.map((section) => ({ section }))
          : sectionList.listSettingCard.items
      );
    } catch (error) {
      console.error('Error refreshing results:', error);
      setResults(LOCAL_CARD_OVERRIDES.filter((card) => card.id !== 'Workspaces'));
      setSections([{ section: 'general' }]);
    }
  }, [callApi]);

  useEffect(() => {
    refreshCardSettings();
  }, [refreshCardSettings]);

  useEffect(() => {
    if (location.hash === '#workspace') {
      document.getElementById('workspace')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [location.pathname, location.hash]);

  const renderIcon = (iconConfig) => {
    if (iconConfig.type === 'svg') {
      return (
        <svg
          width={iconConfig.width || 24}
          height={iconConfig.height || 24}
          viewBox={iconConfig.viewBox || "0 0 24 24"}
          fill="none"
        >
          <path
            d={iconConfig.path}
            stroke="currentColor"
            strokeWidth={iconConfig.strokeWidth || "1.5"}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill={iconConfig.fill || "none"}
          />
        </svg>
      );
    } else if (iconConfig.type === 'class') {
      return <i className={iconConfig.className}></i>;
    } else if (iconConfig.type === 'unicode') {
      return <span>{iconConfig.symbol}</span>;
    }
    return null;
  };

  return (

    <div>
      <Header />

      <div className="settings-container">
        <div className="settings-header">
          <h1>Settings</h1>
        </div>

        <div className="settings-section">
          <h2 className="section-title">Workspace</h2>
          <WorkspaceProfileSection />
        </div>

        {sections.map((section) => {
          const sectionCards = results.filter(
            (card) => card.section === section.section && card.id !== 'Workspaces'
          );
          if (sectionCards.length === 0) return null;

          return (
            <div key={section.section || section.id} className="settings-section">
              <h2 className="section-title">{section.section}</h2>

              <div className="settings-grid">
                {sectionCards.map((card) => (
                  <SettingCard
                    key={card.id}
                    icon={renderIcon(card.icon)}
                    title={card.title}
                    description={card.description}
                    disabled={card.enabled}
                    variant={card.variant}
                    comingSoon={card.comingSoon}
                    comingSoonText={card.comingSoonText}
                    onClick={card.comingSoon ? undefined : () => handleCardClick(card.id)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Settings;